import * as React from "react";
import {
  Badge,
  ButtonToolbar,
  Button,
  Form,
  Card,
  Dropdown,
  ProgressBar,
  Table,
} from "react-bootstrap";
import moment from "moment";
import { Loader } from "lib/components/Loader";
import I18n, { TranslationsObjectKeys } from "lib/I18n";
import style from "./style.module.css";
import { envColors } from "../../constants";
import { observer } from "mobx-react-lite";
import { TimeKey, getStore } from "../../store";
import { useEffect } from "react";

interface LabelAndTextProps {
  label: string;
  text: React.ReactNode;
}

function LabelAndText(props: LabelAndTextProps) {
  return (
    <Form.Group>
      <Form.Label>{props.label}</Form.Label>
      <Form.Text muted>{props.text}</Form.Text>
    </Form.Group>
  );
}

export const LoadStatus = observer(() => {
  const { app, env, loadStatus } = getStore();

  useEffect(() => {
    loadStatus.getLoadStatus();
  }, []);

  const onChangeEnv = (value: string | null) => {
    if (value == null) {
      return;
    }
    env.selected = value;
  };

  const envColor = (value: string) => {
    return envColors[value] || "primary";
  };

  const renderNotDeployedYet = () => {
    return (
      <p>
        {I18n.t("event_master_editor.messages.not_deployed_yet", {
          env: env.selected,
        })}
      </p>
    );
  };

  const renderEnvItems = () => {
    return env.list.map((value) => (
      <Dropdown.Item key={value} eventKey={value}>
        {value}
      </Dropdown.Item>
    ));
  };

  const renderLoadStatus = () => {
    return (
      <form>
        <div>
          <Dropdown className="me-2 d-inline-block" onSelect={onChangeEnv}>
            <Dropdown.Toggle
              id="envdropdown"
              variant={envColor(env.selected)}
              title={env.selected}
              key={env.selected}
            >
              {env.selected}
            </Dropdown.Toggle>
            <Dropdown.Menu>{renderEnvItems()}</Dropdown.Menu>
          </Dropdown>
          {I18n.t("event_master_editor.messages.load_status_env_desc", {
            env: env.selected,
            load_interval: env.selected == "dd1" ? 24 : app.lp1LoadInterval,
          })}
        </div>
        <Form.Group>
          <Form.Check
            checked={loadStatus.enabled}
            label={I18n.t("event_master_editor.label.load_status_enabled")}
            disabled
          />
          <Form.Text muted>
            {I18n.t(
              "event_master_editor.messages.load_status_bigqeury_enable_desc",
            )}
          </Form.Text>
        </Form.Group>
        <LabelAndText
          label={I18n.t("event_master_editor.label.load_status_running")}
          text={
            loadStatus.running
              ? I18n.t("event_master_editor.label.load_job_running")
              : I18n.t("event_master_editor.label.load_job_not_running")
          }
        />
        {renderLoadStatusDetail()}
      </form>
    );
  };

  const convertDateTimeToReadable = (datetime?: string) => {
    if (datetime == null || datetime === "") {
      return "";
    }
    return moment(datetime).toDate().toLocaleString();
  };

  const convertTimeKeyToS3Prefix = (timeKey: TimeKey | undefined) => {
    if (!timeKey) {
      return "";
    }
    const momentDate = moment.utc(timeKey.time);
    const yyyymmdd = momentDate.format("YYYYMMDD");
    const hour = momentDate.format("HH");
    // eslint-disable-next-line:max-line-length
    let prefix = `/posts/${env.selected}/${app.applicationId}/${app.eventId}/day=${yyyymmdd}/`;
    prefix += `${env.selected}-${app.applicationId}-${app.eventId}-${yyyymmdd}-${hour}-`;
    return prefix;
  };

  const renderLoadStatusDetail = () => {
    if (loadStatus.running != null && loadStatus.running) {
      return (
        <div>
          <LabelAndText
            label={I18n.t(
              "event_master_editor.label.load_status_current_started_at",
            )}
            text={convertDateTimeToReadable(loadStatus.currentStartedAt)}
          />
          <LabelAndText
            label={I18n.t(
              "event_master_editor.label.load_status_current_time_key",
            )}
            text={convertTimeKeyToS3Prefix(loadStatus.currentTimeKey)}
          />
          <LabelAndText
            label={I18n.t(
              "event_master_editor.label.load_status_last_time_key",
            )}
            text={convertTimeKeyToS3Prefix(loadStatus.lastTimeKey)}
          />
        </div>
      );
    }
    const loadStatusSecondaryText = loadStatus.lastResult
      ? // eslint-disable-next-line
        I18n.t(
          `event_master_editor.messages.load_status_last_result.${loadStatus.lastResult}` as TranslationsObjectKeys,
        )
      : "";

    return (
      <div>
        <LabelAndText
          label={I18n.t("event_master_editor.label.load_status_last_result")}
          text={loadStatusSecondaryText}
        />
        <LabelAndText
          label={I18n.t(
            "event_master_editor.label.load_status_last_started_at",
          )}
          text={convertDateTimeToReadable(loadStatus.lastStartedAt)}
        />
        <LabelAndText
          label={I18n.t(
            "event_master_editor.label.load_status_last_finished_at",
          )}
          text={convertDateTimeToReadable(loadStatus.lastFinishedAt)}
        />
        <LabelAndText
          label={I18n.t("event_master_editor.label.load_status_last_time_key")}
          text={convertTimeKeyToS3Prefix(loadStatus.lastTimeKey)}
        />
        <LabelAndText
          label={I18n.t("event_master_editor.label.load_status_last_error")}
          text={<pre>{loadStatus.lastError ?? ""}</pre>}
        />
      </div>
    );
  };

  const translateBatchState = (state: string) =>
    I18n.t(
      `event_master_editor.messages.load_status_batch_state.${state}` as TranslationsObjectKeys,
    );

  const translateTaskState = (state: string) =>
    I18n.t(
      `event_master_editor.messages.load_status_task_state.${state}` as TranslationsObjectKeys,
    );

  const renderCurrentBatch = () => {
    const batch = loadStatus.currentBatch;
    if (!batch) {
      return null;
    }

    const completed =
      (batch.taskCounts.succeeded ?? 0) + (batch.taskCounts.expanded ?? 0);
    const total = batch.tasks.length;
    const progress = total === 0 ? 0 : Math.round((completed / total) * 100);

    return (
      <section className={style.batchProgress}>
        <h6>{I18n.t("event_master_editor.label.load_status_current_batch")}</h6>
        <LabelAndText
          label={I18n.t("event_master_editor.label.load_status_batch_state")}
          text={
            <Badge bg="secondary">{translateBatchState(batch.state)}</Badge>
          }
        />
        <LabelAndText
          label={I18n.t("event_master_editor.label.load_status_batch_range")}
          text={`${batch.timeKeyFrom ?? "-"} - ${batch.timeKeyTo}`}
        />
        <div className={style.progressSection}>
          <Form.Label>
            {I18n.t("event_master_editor.label.load_status_task_progress", {
              completed,
              total,
            })}
          </Form.Label>
          <ProgressBar now={progress} label={`${progress}%`} />
        </div>
        <div className="table-responsive">
          <Table striped bordered hover size="sm">
            <thead>
              <tr>
                <th>
                  {I18n.t("event_master_editor.label.load_status_task_state")}
                </th>
                <th>
                  {I18n.t("event_master_editor.label.load_status_task_prefix")}
                </th>
                <th>
                  {I18n.t(
                    "event_master_editor.label.load_status_task_granularity",
                  )}
                </th>
                <th>
                  {I18n.t(
                    "event_master_editor.label.load_status_task_strategy",
                  )}
                </th>
                <th>
                  {I18n.t(
                    "event_master_editor.label.load_status_task_attempts",
                  )}
                </th>
                <th>
                  {I18n.t("event_master_editor.label.load_status_task_error")}
                </th>
              </tr>
            </thead>
            <tbody>
              {batch.tasks.map((task) => (
                <tr key={task.id}>
                  <td>{translateTaskState(task.state)}</td>
                  <td className={style.prefix}>{task.prefix}</td>
                  <td>{task.granularity}</td>
                  <td>{task.strategy}</td>
                  <td>{task.attempts}</td>
                  <td>
                    <pre>{task.lastError ?? ""}</pre>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
        {batch.lastError && (
          <LabelAndText
            label={I18n.t("event_master_editor.label.load_status_batch_error")}
            text={<pre>{batch.lastError}</pre>}
          />
        )}
      </section>
    );
  };

  return (
    <div className={style.root}>
      <Loader show={loadStatus.isLoading}>
        <Card>
          <Card.Header>
            {I18n.t("event_master_editor.messages.load_status_desc")}
          </Card.Header>
          <Card.Body>
            {!loadStatus.isLoading && !loadStatus.deployed ? (
              renderNotDeployedYet()
            ) : (
              <>
                {renderLoadStatus()}
                {renderCurrentBatch()}
              </>
            )}
            <ButtonToolbar>
              <Button
                className="me-2"
                onClick={() => loadStatus.getLoadStatus()}
              >
                {I18n.t("event_master_editor.action.reload")}
              </Button>
            </ButtonToolbar>
          </Card.Body>
        </Card>
      </Loader>
    </div>
  );
});
